<script lang="ts">
  /**
   * Root layout: header/nav, router outlet, and footer notices.
   * Pseudocode:
   * - Load site config and app manifest
   * - Derive current route from hash and display either Home or App page
   * - Provide search/filter over apps on Home
   */
  import { onMount } from 'svelte';
  import Home from '$lib/views/Home.svelte';
  import StorageApps from '$lib/views/StorageApps.svelte';
  import Privacy from '$lib/views/Privacy.svelte';
  import Terms from '$lib/views/Terms.svelte';
  import Accessibility from '$lib/views/Accessibility.svelte';
  import About from '$lib/views/About.svelte';
  import Feedback from '$lib/views/Feedback.svelte';
  import Settings from '$lib/views/Settings.svelte';
  import PrivacyProof from '$lib/views/PrivacyProof.svelte';
  import AppHost from '$apps/AppHost.svelte';
  import { route, setHashRouter } from '$lib/router';
  import { site } from '$lib/config/site.config';
  import Logo from '$lib/components/Logo.svelte';
  import { canInstall, promptInstall } from '$lib/state/pwa';
  import { updateAvailable, applyUpdate } from '$lib/state/sw';
  import { ToastContainer } from '$lib/components/common';
  import { theme } from '$lib/state/theme';

  let scrolled = false;
  let mobileMenuOpen = false;
  $: isWideApp = $route.kind === 'app' && $route.id === 'local-ai-workspace';

  // Close mobile menu on route change
  $: if ($route) closeMobileMenu();

  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  function closeMobileMenu() {
    mobileMenuOpen = false;
    document.body.style.overflow = '';
  }

  // Initialize hash-based router and theme on mount
  onMount(() => {
    setHashRouter();
    theme.init();

    const onScroll = () => { scrolled = window.scrollY > 10; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.body.style.overflow = '';
    };
  });
</script>

<header class="site-header" class:scrolled class:menu-open={mobileMenuOpen}>
  <div class="header-inner">
    <a href="#/" class="brand" on:click={closeMobileMenu}>
      <Logo className="w-7 h-7" />
      <span class="brand-name">{site.brandName}</span>
    </a>

    <!-- Desktop Nav -->
    <nav class="header-nav desktop-nav">
      <a href="#/" class="nav-link" class:active={$route.kind === 'home'}>All Tools</a>
      <a href="#/about" class="nav-link" class:active={$route.kind === 'about'}>About</a>
      <a href="#/privacy-proof" class="nav-link privacy-link" class:active={$route.kind === 'privacy-proof'}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        Privacy Proof
      </a>
      {#if $canInstall}
        <button class="nav-link install-btn" on:click={promptInstall}>
          Install
        </button>
      {/if}
      {#if $updateAvailable}
        <button class="nav-link update-btn" on:click={applyUpdate}>Update</button>
      {/if}
    </nav>

    <div class="header-right">
      <button class="theme-toggle" on:click={theme.toggle} aria-label="Toggle theme" title="Toggle light/dark mode">
        {#if $theme === 'dark'}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        {:else}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
        {/if}
      </button>

      <!-- Mobile hamburger -->
      <button class="mobile-menu-btn" on:click={toggleMobileMenu} aria-label="Toggle menu" aria-expanded={mobileMenuOpen}>
        <span class="hamburger-line" class:open={mobileMenuOpen}></span>
        <span class="hamburger-line" class:open={mobileMenuOpen}></span>
        <span class="hamburger-line" class:open={mobileMenuOpen}></span>
      </button>
    </div>
  </div>
</header>

<!-- Mobile Nav Overlay -->
{#if mobileMenuOpen}
  <div class="mobile-overlay" on:click={closeMobileMenu} on:keydown={(e) => e.key === 'Escape' && closeMobileMenu()} role="button" tabindex="-1" aria-label="Close menu"></div>
{/if}

<nav class="mobile-nav" class:open={mobileMenuOpen}>
  <div class="mobile-nav-content">
    <div class="mobile-nav-section">
      <div class="mobile-nav-label">Navigation</div>
      <a href="#/" class="mobile-nav-link" class:active={$route.kind === 'home'} on:click={closeMobileMenu}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
        All Tools
      </a>
      <a href="#/about" class="mobile-nav-link" class:active={$route.kind === 'about'} on:click={closeMobileMenu}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4m0-4h.01"/></svg>
        About
      </a>
      <a href="#/privacy-proof" class="mobile-nav-link" class:active={$route.kind === 'privacy-proof'} on:click={closeMobileMenu}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        Privacy Proof
      </a>
      <a href="#/settings" class="mobile-nav-link" class:active={$route.kind === 'settings'} on:click={closeMobileMenu}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
        Settings
      </a>
      <a href="#/feedback" class="mobile-nav-link" class:active={$route.kind === 'feedback'} on:click={closeMobileMenu}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        Feedback
      </a>
    </div>

    {#if $canInstall}
      <div class="mobile-nav-section">
        <button class="mobile-nav-link install-link" on:click={() => { promptInstall(); closeMobileMenu(); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Install App
        </button>
      </div>
    {/if}

    {#if $updateAvailable}
      <div class="mobile-nav-section">
        <button class="mobile-nav-link update-link" on:click={() => { applyUpdate(); closeMobileMenu(); }}>
          Update Available
        </button>
      </div>
    {/if}

    <div class="mobile-nav-footer">
      <span class="mobile-nav-privacy-tag">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        100% local · No tracking
      </span>
    </div>
  </div>
</nav>

<main class="site-main">
  {#if $route.kind === 'home'}
    <Home />
  {:else}
    <div class="content-container" class:wide-app={isWideApp}>
      {#if $route.kind === 'storage-apps'}
        <StorageApps />
      {:else if $route.kind === 'privacy'}
        <Privacy />
      {:else if $route.kind === 'terms'}
        <Terms />
      {:else if $route.kind === 'accessibility'}
        <Accessibility />
      {:else if $route.kind === 'about'}
        <About />
      {:else if $route.kind === 'feedback'}
        <Feedback />
      {:else if $route.kind === 'settings'}
        <Settings />
      {:else if $route.kind === 'privacy-proof'}
        <PrivacyProof />
      {:else if $route.kind === 'app'}
        <AppHost id={$route.id} />
      {/if}
    </div>
  {/if}
</main>

<footer class="site-footer">
  <div class="footer-glow-line"></div>
  <div class="footer-inner">
    <div class="footer-brand">
      <div class="footer-brand-row">
        <Logo className="w-6 h-6" />
        <span class="footer-brand-name">{site.brandName}</span>
      </div>
      <p class="footer-tagline">Privacy-first browser tools.<br/>No uploads. No tracking. Free forever.</p>
    </div>
    <div class="footer-links">
      <div class="footer-col">
        <div class="footer-col-title">Product</div>
        <a href="#/">All Tools</a>
        <a href="#/about">About</a>
        <a href="#/feedback">Feedback</a>
        <a href="#/privacy-proof">Privacy Proof</a>
        <a href="#/settings">Settings</a>
      </div>
      <div class="footer-col">
        <div class="footer-col-title">Legal</div>
        <a href="#/privacy">Privacy</a>
        <a href="#/terms">Terms</a>
        <a href="#/accessibility">Accessibility</a>
      </div>
    </div>
  </div>
  <div class="footer-bottom">
    <span>© {new Date().getFullYear()} {site.brandName}. All rights reserved.</span>
    <span class="footer-updated">Last updated {site.lastUpdated}</span>
  </div>
</footer>

<!-- Toast notification container -->
<ToastContainer />

<style>
  /* ==================== HEADER ==================== */
  .site-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: hsl(var(--theme-background) / 0.8);
    backdrop-filter: blur(20px) saturate(1.4);
    -webkit-backdrop-filter: blur(20px) saturate(1.4);
    transition: box-shadow 300ms var(--theme-ease), border-color 300ms var(--theme-ease), background 300ms var(--theme-ease);
    border-bottom: 1px solid transparent;
  }
  .site-header.scrolled {
    border-bottom-color: hsl(var(--theme-border) / 0.6);
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.06), 0 4px 12px rgb(0 0 0 / 0.04);
    background: hsl(var(--theme-background) / 0.92);
  }
  .header-inner {
    max-width: 76rem;
    margin: 0 auto;
    padding: 0 1.5rem;
    height: 3.5rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  /* Brand */
  .brand {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    text-decoration: none;
    transition: opacity 200ms ease;
    flex-shrink: 0;
  }
  .brand:hover { opacity: 0.8; }
  .brand-name {
    font-family: 'Inter', sans-serif;
    font-size: 1.0625rem;
    font-weight: 700;
    letter-spacing: -0.025em;
    color: hsl(var(--theme-foreground));
  }

  /* Desktop Nav */
  .desktop-nav {
    display: flex;
    align-items: center;
    gap: 0.125rem;
  }
  .nav-link {
    padding: 0.4375rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: hsl(var(--theme-muted-foreground));
    text-decoration: none;
    transition: color 200ms ease, background 200ms ease;
    border: none;
    background: none;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    border-radius: var(--theme-radius-sm);
  }
  .nav-link:hover {
    color: hsl(var(--theme-foreground));
    background: hsl(var(--theme-muted) / 0.1);
  }
  .nav-link.active {
    color: hsl(var(--theme-foreground));
    font-weight: 600;
    background: hsl(var(--theme-muted) / 0.12);
  }
  .privacy-link {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }
  .install-btn, .update-btn {
    color: hsl(var(--theme-primary));
    font-weight: 600;
  }

  /* Right side */
  .header-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
  .theme-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: var(--theme-radius-sm);
    border: 1px solid hsl(var(--theme-border) / 0.6);
    background: transparent;
    color: hsl(var(--theme-muted-foreground));
    cursor: pointer;
    transition: all 200ms ease;
  }
  .theme-toggle:hover {
    color: hsl(var(--theme-foreground));
    background: hsl(var(--theme-muted) / 0.15);
    border-color: hsl(var(--theme-border));
  }

  /* ==================== HAMBURGER BUTTON ==================== */
  .mobile-menu-btn {
    display: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4.5px;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    border: 1px solid hsl(var(--theme-border) / 0.6);
    border-radius: var(--theme-radius-sm);
    background: transparent;
    cursor: pointer;
    transition: background 200ms ease;
  }
  .mobile-menu-btn:hover {
    background: hsl(var(--theme-muted) / 0.15);
  }
  .hamburger-line {
    display: block;
    width: 16px;
    height: 1.5px;
    background: hsl(var(--theme-foreground));
    border-radius: 2px;
    transition: all 300ms var(--theme-ease);
    transform-origin: center;
  }
  .hamburger-line.open:nth-child(1) {
    transform: translateY(6px) rotate(45deg);
  }
  .hamburger-line.open:nth-child(2) {
    opacity: 0;
    transform: scaleX(0);
  }
  .hamburger-line.open:nth-child(3) {
    transform: translateY(-6px) rotate(-45deg);
  }

  /* ==================== MOBILE NAV OVERLAY ==================== */
  .mobile-overlay {
    display: none;
    position: fixed;
    inset: 0;
    z-index: 90;
    background: rgb(0 0 0 / 0.4);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    animation: overlay-in 300ms var(--theme-ease);
  }
  @keyframes overlay-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* ==================== MOBILE NAV SHEET ==================== */
  .mobile-nav {
    display: none;
    position: fixed;
    top: 3.5rem;
    right: 0;
    bottom: 0;
    width: min(320px, 85vw);
    z-index: 95;
    background: hsl(var(--theme-background));
    border-left: 1px solid hsl(var(--theme-border) / 0.5);
    box-shadow: -8px 0 30px rgb(0 0 0 / 0.15);
    transform: translateX(100%);
    transition: transform 350ms var(--theme-ease);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  .mobile-nav.open {
    transform: translateX(0);
  }
  .mobile-nav-content {
    padding: 1.25rem 1.25rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-height: 100%;
  }
  .mobile-nav-section {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .mobile-nav-label {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: hsl(var(--theme-muted-foreground) / 0.7);
    padding: 0 0.75rem;
    margin-bottom: 0.375rem;
  }
  .mobile-nav-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: var(--theme-radius);
    font-size: 0.9375rem;
    font-weight: 500;
    color: hsl(var(--theme-muted-foreground));
    text-decoration: none;
    transition: all 200ms ease;
    border: none;
    background: none;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    width: 100%;
    text-align: left;
    min-height: 2.75rem;
  }
  .mobile-nav-link:hover,
  .mobile-nav-link:active {
    color: hsl(var(--theme-foreground));
    background: hsl(var(--theme-muted) / 0.12);
  }
  .mobile-nav-link.active {
    color: hsl(var(--theme-primary));
    background: hsl(var(--theme-primary) / 0.08);
    font-weight: 600;
  }
  .mobile-nav-link.install-link {
    color: hsl(var(--theme-primary));
    font-weight: 600;
  }
  .mobile-nav-link.update-link {
    color: hsl(var(--theme-accent));
    font-weight: 600;
  }
  .mobile-nav-footer {
    margin-top: auto;
    padding-top: 1.5rem;
    border-top: 1px solid hsl(var(--theme-border) / 0.3);
  }
  .mobile-nav-privacy-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: hsl(var(--theme-muted-foreground) / 0.7);
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    background: hsl(var(--theme-primary) / 0.06);
    border: 1px solid hsl(var(--theme-primary) / 0.12);
  }

  /* ==================== MAIN ==================== */
  .site-main {
    min-height: 70vh;
    background-color: hsl(var(--theme-background));
    color: hsl(var(--theme-foreground));
  }
  .content-container {
    max-width: 72rem;
    margin: 0 auto;
    padding: 1.5rem 1.5rem;
  }
  .content-container.wide-app {
    max-width: 92rem;
  }

  /* ==================== FOOTER ==================== */
  .site-footer {
    position: relative;
    margin-top: 5rem;
    border-top: none;
  }
  .footer-glow-line {
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      hsl(var(--theme-primary) / 0.3) 20%,
      hsl(var(--theme-primary) / 0.5) 50%,
      hsl(var(--theme-primary) / 0.3) 80%,
      transparent 100%
    );
  }
  .footer-inner {
    max-width: 76rem;
    margin: 0 auto;
    padding: 3rem 1.5rem 2rem;
    display: flex;
    justify-content: space-between;
    gap: 3rem;
    flex-wrap: wrap;
  }
  .footer-brand {
    max-width: 280px;
  }
  .footer-brand-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  .footer-brand-name {
    font-weight: 700;
    font-size: 1rem;
    color: hsl(var(--theme-foreground));
    letter-spacing: -0.02em;
  }
  .footer-tagline {
    font-size: 0.8125rem;
    line-height: 1.6;
    color: hsl(var(--theme-muted-foreground));
  }
  .footer-links {
    display: flex;
    gap: 3rem;
  }
  .footer-col {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .footer-col-title {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: hsl(var(--theme-muted-foreground));
    margin-bottom: 0.375rem;
  }
  .footer-col a {
    font-size: 0.8125rem;
    color: hsl(var(--theme-muted-foreground));
    text-decoration: none;
    transition: color 200ms ease;
  }
  .footer-col a:hover { color: hsl(var(--theme-foreground)); }
  .footer-bottom {
    max-width: 76rem;
    margin: 0 auto;
    padding: 1.5rem 1.5rem;
    border-top: 1px solid hsl(var(--theme-border) / 0.3);
    font-size: 0.75rem;
    color: hsl(var(--theme-muted-foreground) / 0.7);
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .footer-updated {
    color: hsl(var(--theme-muted-foreground) / 0.55);
  }

  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 768px) {
    .desktop-nav { display: none; }
    .mobile-menu-btn { display: flex; }
    .mobile-overlay { display: block; }
    .mobile-nav { display: block; }

    .header-inner { padding: 0 1rem; height: 3.25rem; }
    .brand-name { font-size: 0.9375rem; }
    .mobile-nav { top: 3.25rem; }

    .content-container { padding: 1rem 1rem; }
    .footer-inner { flex-direction: column; gap: 2rem; padding: 2.5rem 1rem 1.5rem; }
    .footer-brand { max-width: 100%; }
    .footer-bottom { flex-direction: column; gap: 0.4rem; padding: 1.25rem 1rem; }
  }
</style>
